// src/merchant/MerchantProductForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, addDoc, doc, getDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { useApp } from '../context/AppContext';
import { Upload, X, ArrowLeft, Save, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────
// All 11 product type categories — shown as MULTI-SELECT checkboxes
// ─────────────────────────────────────────────────────────────────
const PRODUCT_CATEGORIES = [
  { value: 'personalized', label: 'Personalized',      icon: '✨' },
  { value: 'handmade',     label: 'Handmade',          icon: '🤝' },
  { value: 'surprise',     label: 'Surprise Box',      icon: '📦' },
  { value: 'anniversary',  label: 'Anniversary',       icon: '💍' },
  { value: 'valentines',   label: "Valentine's",        icon: '❤️' },
  { value: 'jewelry',      label: 'Jewellery',         icon: '💎' },
  { value: 'clothing',     label: 'Clothing',          icon: '👗' },
  { value: 'home-decor',   label: 'Home Décor',        icon: '🏠' },
  { value: 'food',         label: 'Food & Sweets',     icon: '🍫' },
  { value: 'stationery',   label: 'Cards & Stationery',icon: '📝' },
  { value: 'experience',   label: 'Experiences',       icon: '🎉' },
];

// ─────────────────────────────────────────────────────────────────
// All 12 recipient options — multi-select checkboxes
// ─────────────────────────────────────────────────────────────────
const RECIPIENT_OPTIONS = [
  { value: 'girlfriend',    label: 'For Girlfriend',    icon: '💝' },
  { value: 'boyfriend',     label: 'For Boyfriend',     icon: '💙' },
  { value: 'wife',          label: 'For Wife',          icon: '💍' },
  { value: 'husband',       label: 'For Husband',       icon: '🤵' },
  { value: 'female-friend', label: 'For Female Friend', icon: '👭' },
  { value: 'male-friend',   label: 'For Male Friend',   icon: '👬' },
  { value: 'sister',        label: 'For Sister',        icon: '👧' },
  { value: 'brother',       label: 'For Brother',       icon: '👦' },
  { value: 'mother',        label: 'For Mother',        icon: '👩' },
  { value: 'father',        label: 'For Father',        icon: '👨' },
  { value: 'couple',        label: 'For Couples',       icon: '👫' },
  { value: 'anniversary',   label: 'Anniversary',       icon: '🥂' },
];

const EMPTY_FORM = {
  name: '', description: '', price: '', originalPrice: '',
  stock: '', badge: '', tags: '',
  isNew: false, isFeatured: false, isActive: true,
};

const inputCls = "w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-gray-800 text-sm bg-gray-50 focus:bg-white transition-all";

// ── All components OUTSIDE the main component (prevents remount typing bug) ──

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-body font-semibold text-gray-700 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 font-body mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({ checked, onChange, label, sub }) {
  return (
    <div className="flex items-center justify-between px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors">
      <div>
        <p className="font-body font-medium text-gray-800 text-sm">{label}</p>
        {sub && <p className="font-body text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={checked}
        className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none shrink-0 ${checked ? 'bg-rose-500' : 'bg-gray-200'}`}
      >
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${checked ? 'left-6' : 'left-1'}`} />
      </button>
    </div>
  );
}

// ── Reusable pill-style checkbox grid ─────────────────────────────
function PillCheckboxGroup({ options, selected, onChange }) {
  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map(({ value, label, icon }) => {
        const active = selected.includes(value);
        return (
          <button
            key={value}
            type="button"
            onClick={() => toggle(value)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-body text-left transition-all ${
              active
                ? 'border-rose-400 bg-rose-50 text-rose-700 font-semibold shadow-sm'
                : 'border-gray-200 bg-white text-gray-600 hover:border-rose-300 hover:bg-rose-50/50'
            }`}
          >
            <span className="text-base shrink-0">{icon}</span>
            <span className="leading-tight">{label}</span>
            {active && <span className="ml-auto text-rose-500 text-xs shrink-0">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function MerchantProductForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const isEdit   = Boolean(id);

  const [form,           setForm]           = useState(EMPTY_FORM);
  const [categories,     setCategories]     = useState([]);   // multi-select
  const [recipients,     setRecipients]     = useState([]);   // multi-select
  const [images,         setImages]         = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [fetchLoading,   setFetchLoading]   = useState(isEdit);
  const [uploadProgress, setUploadProgress] = useState({});

  // Load product for edit
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'products', id));
        if (!snap.exists()) {
          toast.error('Product not found');
          navigate('/merchant/products');
          return;
        }
        const d = snap.data();
        setForm({
          name:          d.name          || '',
          description:   d.description   || '',
          price:         d.price         || '',
          originalPrice: d.originalPrice || '',
          stock:         d.stock         || '',
          badge:         d.badge         || '',
          tags:          (d.tags || []).join(', '),
          isNew:         d.isNew         || false,
          isFeatured:    d.isFeatured    || false,
          isActive:      d.isActive !== false,
        });
        // Support old single `category` string OR new `categories` array
        if (Array.isArray(d.categories) && d.categories.length) {
          setCategories(d.categories);
        } else if (d.category) {
          setCategories([d.category]);
        }
        setRecipients(d.recipient || []);
        const imgs = (d.images?.length ? d.images : d.image ? [d.image] : []).map((url) => ({ url }));
        setImages(imgs);
      } catch {
        toast.error('Failed to load product');
      } finally {
        setFetchLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleToggle = (field) => () =>
    setForm((prev) => ({ ...prev, [field]: !prev[field] }));

  // Image upload
  const uploadImage = (file, index) =>
    new Promise((resolve, reject) => {
      const storageRef = ref(storage, `products/${Date.now()}_${file.name}`);
      const task       = uploadBytesResumable(storageRef, file);
      task.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadProgress((p) => ({ ...p, [index]: pct }));
        },
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          setUploadProgress((p) => { const n = { ...p }; delete n[index]; return n; });
          resolve(url);
        }
      );
    });

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 4) { toast.error('Maximum 4 images'); return; }
    setImages((prev) => [...prev, ...files.map((f) => ({ url: URL.createObjectURL(f), file: f }))]);
    e.target.value = '';
  };

  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())                { toast.error('Product name is required');        return; }
    if (!form.description.trim())         { toast.error('Description is required');          return; }
    if (!form.price || isNaN(form.price)) { toast.error('Valid price is required');          return; }
    if (!form.stock || isNaN(form.stock)) { toast.error('Stock quantity is required');       return; }
    if (categories.length === 0)          { toast.error('Select at least 1 category');       return; }
    if (images.length === 0)              { toast.error('Upload at least 1 image');          return; }

    setLoading(true);
    try {
      const uploadedImages = await Promise.all(
        images.map((img, i) => img.file ? uploadImage(img.file, i) : Promise.resolve(img.url))
      );

      const productData = {
        name:          form.name.trim(),
        description:   form.description.trim(),
        price:         parseFloat(form.price),
        originalPrice: parseFloat(form.originalPrice) || parseFloat(form.price),
        categories,                          // array of selected categories
        category:      categories[0] || '',  // keep first for backwards compat
        recipient:     recipients,
        stock:         parseInt(form.stock, 10),
        badge:         form.badge.trim(),
        tags:          form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        isNew:         form.isNew,
        isFeatured:    form.isFeatured,
        isActive:      form.isActive,
        image:         uploadedImages[0],
        images:        uploadedImages,
        merchantId:    user.uid,
      };

      if (isEdit) {
        await updateDoc(doc(db, 'products', id), { ...productData, updatedAt: serverTimestamp() });
        toast.success('Product updated! ✅');
      } else {
        await addDoc(collection(db, 'products'), { ...productData, rating: 0, reviews: 0, createdAt: serverTimestamp() });
        toast.success('Product added! 🎉');
      }
      navigate('/merchant/products');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save. Check Firebase Storage is enabled.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => navigate('/merchant/products')}
          className="w-9 h-9 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:border-rose-300 transition-colors">
          <ArrowLeft size={16} className="text-gray-600" />
        </button>
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h2>
          <p className="text-gray-400 font-body text-sm">
            {isEdit ? 'Update your product details' : 'Fill in all details to list your product'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── Images ── */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h3 className="font-body font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <ImageIcon size={18} className="text-rose-400" /> Product Images
          </h3>
          <p className="text-xs text-gray-400 font-body mb-4">
            Upload up to 4 photos. First photo is the main display image.
          </p>
          <div className="grid grid-cols-4 gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-2xl overflow-hidden border-2 border-rose-200 bg-gray-50">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                {uploadProgress[i] !== undefined && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">{uploadProgress[i]}%</span>
                  </div>
                )}
                {i === 0 && (
                  <span className="absolute top-1 left-1 bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">MAIN</span>
                )}
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                  <X size={12} />
                </button>
              </div>
            ))}
            {images.length < 4 && (
              <label className="aspect-square rounded-2xl border-2 border-dashed border-gray-300 hover:border-rose-400 flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-rose-50 group transition-colors">
                <Upload size={20} className="text-gray-400 group-hover:text-rose-400 mb-1" />
                <span className="text-xs text-gray-400 group-hover:text-rose-400 font-body">Upload</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />
              </label>
            )}
          </div>
        </div>

        {/* ── Basic Info ── */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm space-y-4">
          <h3 className="font-body font-semibold text-gray-900">Basic Information</h3>

          <Field label="Product Name" required>
            <input type="text" value={form.name} onChange={handleChange('name')}
              placeholder="e.g. Handmade Floral Bookmark" className={inputCls} autoComplete="off" />
          </Field>

          <Field label="Description" required hint="Describe materials, size, what's included, what makes it special">
            <textarea value={form.description} onChange={handleChange('description')} rows={4}
              placeholder="A beautiful hand-painted bookmark crafted with love..."
              className={`${inputCls} resize-none`} />
          </Field>

          <Field label="Badge" hint="Short label on product card — e.g. Bestseller, New, Handmade">
            <input type="text" value={form.badge} onChange={handleChange('badge')}
              placeholder="e.g. Handmade" className={inputCls} autoComplete="off" />
          </Field>

          <Field label="Search Tags" hint="Comma separated keywords — helps customers find you">
            <input type="text" value={form.tags} onChange={handleChange('tags')}
              placeholder="e.g. bookmark, floral, handmade, romantic, gift" className={inputCls} autoComplete="off" />
          </Field>
        </div>

        {/* ── Product Categories (multi-select) ── */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-body font-semibold text-gray-900">Product Category</h3>
            {categories.length > 0 && (
              <span className="text-xs bg-rose-100 text-rose-600 font-bold px-2.5 py-1 rounded-full">
                {categories.length} selected
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 font-body mb-4">
            Select all categories that apply — you can pick more than one.
          </p>
          <PillCheckboxGroup
            options={PRODUCT_CATEGORIES}
            selected={categories}
            onChange={setCategories}
          />
          {categories.length === 0 && (
            <p className="text-xs text-amber-500 font-body mt-2">⚠ Please select at least one category</p>
          )}
        </div>

        {/* ── Recipients (multi-select) ── */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-body font-semibold text-gray-900">Who Is This Gift For?</h3>
            {recipients.length > 0 && (
              <span className="text-xs bg-rose-100 text-rose-600 font-bold px-2.5 py-1 rounded-full">
                {recipients.length} selected
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 font-body mb-4">
            Select all that apply — helps customers find your product in the right category.
          </p>
          <PillCheckboxGroup
            options={RECIPIENT_OPTIONS}
            selected={recipients}
            onChange={setRecipients}
          />
        </div>

        {/* ── Pricing & Stock ── */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm space-y-4">
          <h3 className="font-body font-semibold text-gray-900">Pricing & Stock</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Selling Price (₹)" required>
              <input type="number" value={form.price} onChange={handleChange('price')}
                placeholder="100" min="0" step="1" className={inputCls} />
            </Field>
            <Field label="Original Price (₹)" hint="Shows strikethrough if higher">
              <input type="number" value={form.originalPrice} onChange={handleChange('originalPrice')}
                placeholder="149" min="0" step="1" className={inputCls} />
            </Field>
            <Field label="Stock Quantity" required>
              <input type="number" value={form.stock} onChange={handleChange('stock')}
                placeholder="10" min="0" step="1" className={inputCls} />
            </Field>
          </div>
        </div>

        {/* ── Visibility ── */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h3 className="font-body font-semibold text-gray-900 mb-3">Visibility & Flags</h3>
          <div className="divide-y divide-gray-50">
            <Toggle checked={form.isActive}   onChange={handleToggle('isActive')}
              label="Product is Live" sub="Visible to customers on the store" />
            <Toggle checked={form.isFeatured} onChange={handleToggle('isFeatured')}
              label="Featured on Homepage" sub="Show in Bestselling section" />
            <Toggle checked={form.isNew}      onChange={handleToggle('isNew')}
              label="Mark as New Arrival" sub="Show in New Arrivals section" />
          </div>
        </div>

        {/* ── Submit ── */}
        <div className="flex items-center gap-3 pb-10">
          <button type="submit" disabled={loading}
            className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-8 py-3.5 rounded-2xl font-body font-semibold transition-all shadow-[0_4px_15px_rgba(244,63,94,0.3)] disabled:opacity-60">
            {loading
              ? <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              : <Save size={18} />
            }
            {loading ? 'Saving...' : isEdit ? 'Update Product' : 'Add Product'}
          </button>
          <button type="button" onClick={() => navigate('/merchant/products')}
            className="px-6 py-3.5 rounded-2xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-body font-medium transition-all">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
